import {
  AddIcon,
  BoardIcon,
  CloseIcon,
  FilterTickMarkIcon,
  LabelIcon,
  PersonIcon,
  SelectIcon,
} from '@sketchnote/icons';
import {
  LabelSelectNoOptions,
  LabelSelectOption,
  LabelSelectSingleValue,
  labelSelectStyles,
  MoreActionMenu,
  Switch,
  UserSelectDropDownIndicator,
  UserSelectMultiValueLabel,
  UserSelectOption,
  UserSelectPlaceholder,
  UserSelectSingleValue,
} from '@sketchnote/shared-components';
import cn from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import Select from 'react-select';
import {
  PropertySelectOption,
  PropertySelectSingleValue,
  PropertySelectStyles,
} from 'src/components/Elements/SelectStyles/Property';
import { useFileStore } from 'src/features/files';
import { useKanbanStore } from 'src/features/kanban';
import useOnClickOutside from 'use-onclickoutside';
import { shallow } from 'zustand/shallow';

import { Filter, useBoardStore } from '../store/useBoardStore';

const { useEffect, useMemo, useCallback } = React;

export const Filters = ({ onClose }: { onClose: () => void }) => {
  const { labels } = useKanbanStore((s) => ({
    labels: s.labels,
  }));
  const [incompleteFilters, setIncompleteFilters] = React.useState<Filter[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    filters,
    onAddFilter,
    onChangeFilter,
    onChangeFilterBehaviour,
    onChangeFilterProperty,
    onRemoveFilter,
    onShowHideQuickFilter,
    showQuickFilter,
  } = useBoardStore(
    (s) => ({
      filters: s.filters,
      onAddFilter: s.onAddFilter,
      onChangeFilter: s.onChangeFilter,
      onChangeFilterBehaviour: s.onChangeFilterBehaviour,
      onChangeFilterProperty: s.onChangeFilterProperty,
      onRemoveFilter: s.onRemoveFilter,
      onShowHideQuickFilter: s.onShowHideQuickFilter,
      showQuickFilter: s.showQuickFilter,
    }),
    shallow
  );
  const router = useRouter();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const viewId = router.query.viewId as string;
  const fileId = router.query.slug as string;

  const handleOnAddFilter = (filterOption) => {
    setIncompleteFilters([...incompleteFilters, { filter: filterOption.value, behaviour: 'is' }]);
  };
  const fileData = useFileStore((s) => s.files[fileId]);
  const viewData =
    fileData?.internalFiles?.find((obj) => obj.viewId === viewId) || fileData?.primaryFile;
  const viewFilters = viewData?.config?.filters || [];
  const updateViewConfig = useFileStore((s) => s.onUpdateViewConfig);

  const isPrimaryFile = useMemo(
    () => viewId === fileData?.primaryFile?.id,
    [fileData?.primaryFile?.id, viewId]
  );

  useEffect(() => {
    if (viewFilters.length) {
      useBoardStore.setState((s) => {
        s.filters = viewFilters;
      });
    }
  }, [viewId]);

  useEffect(() => {
    if (viewId)
      updateViewConfig({
        viewId: viewId,
        config: { ...(viewData?.config || {}), filters: filters },
        fileId: fileId,
        isPrimaryFile,
      });
  }, [filters]);

  useOnClickOutside(containerRef, onClose);

  const handleRemoveFilter = (index: number, isIncomplete: boolean) => () => {
    if (isIncomplete) {
      setIncompleteFilters(incompleteFilters.filter((_, i) => i !== index));
    } else {
      onRemoveFilter(index);
    }
    if (filters.length + incompleteFilters.length === 1) {
      onClose();
    }
  };

  const handleChangeFilter = (index: number, isIncomplete: boolean) => (filter) => {
    if (isIncomplete) {
      setIncompleteFilters(
        incompleteFilters.map((obj, i) => {
          if (i === index) {
            return {
              ...obj,
              filter: filter.value,
            };
          }
          return obj;
        })
      );
      return;
    } else onChangeFilter(index, filter.value);
  };

  const handleChangeBehaviour = (index: number, isIncomplete: boolean) => (behaviour) => {
    if (isIncomplete) {
      if (behaviour.value === 'is_set' || behaviour.value === 'is_not_set') {
        setIncompleteFilters(incompleteFilters.filter((_, i) => i !== index));
        onAddFilter({
          ...incompleteFilters[index],
          behaviour: behaviour.value,
        });
        return;
      } else {
        setIncompleteFilters(
          incompleteFilters.map((obj, i) => {
            if (i === index) {
              return {
                ...obj,
                behaviour: behaviour.value,
              };
            }
            return obj;
          })
        );
        return;
      }
    } else onChangeFilterBehaviour(index, behaviour.value);
  };

  const handleChangeProperty = (index: number, isIncomplete: boolean) => (property) => {
    if (isIncomplete) {
      const filter = incompleteFilters[index];
      setIncompleteFilters(incompleteFilters.filter((_, i) => i !== index));
      if (filter.filter === 'label') {
        onAddFilter({
          ...filter,
          property: {
            label: property.label,
            id: property.id,
            color: property.color,
          },
        });
      }
      onAddFilter({
        ...filter,
        property: property.value,
      });
      return;
    } else {
      if (filters[index].filter === 'label') {
        onChangeFilterProperty(index, {
          label: property.label,
          id: property.id,
          color: property.color,
        });
      } else onChangeFilterProperty(index, property.value);
    }
  };

  const assigneeOptions = React.useMemo(
    () =>
      currentFile?.membersWithAccess
        ? currentFile?.membersWithAccess?.map((obj) => ({
            label: obj.firstname,
            value: obj.memberId,
            memberId: obj.memberId,
            avatar: obj.avatar,
          }))
        : [],
    [currentFile?.membersWithAccess]
  );

  return (
    <div className="rounded-lg bg-white px-16 py-8 shadow-sm min-w-600" ref={containerRef}>
      <div className="flex items-center justify-between text-sm text-gray-500 border-b border-gray-200 pb-12 pt-6">
        <div className="flex items-center">
          <span className="mr-6">Active filters for </span>
          <BoardIcon />
          <span className="font-medium ml-6">Board</span>
        </div>
        <div className="flex items-center">
          <span className="mr-8">Show Quick filters</span>
          <Switch
            checked={showQuickFilter}
            onChange={() => {
              onShowHideQuickFilter(!showQuickFilter);
            }}
          />
        </div>
      </div>
      <div>
        {[filters, incompleteFilters].map((filterList, listIndex) => {
          const isIncomplete = listIndex === 1;
          return filterList.map((filter, index) => {
            const isFirstFilter =
              (listIndex === 0 && index === 0) || (!filters.length && index === 0);
            return (
              <div className="flex items-center w-full py-6" key={`${filter.filter}-${index}`}>
                <div className="w-full mr-16">
                  {isFirstFilter && (
                    <label className="mb-14 block text-gray-500 font-medium" htmlFor="assigner">
                      Filter property
                    </label>
                  )}
                  <Select
                    options={['assigner', 'assignee', 'priority', 'status', 'label'].map((obj) => ({
                      label: propertyLabelIcon[obj].label,
                      value: obj,
                      icon: propertyLabelIcon[obj].icon,
                    }))}
                    onChange={handleChangeFilter(index, isIncomplete)}
                    menuPortalTarget={document.body}
                    value={
                      filter.filter
                        ? {
                            label: propertyLabelIcon[filter.filter].label,
                            value: filter.filter,
                            icon: propertyLabelIcon[filter.filter].icon,
                          }
                        : undefined
                    }
                    styles={PropertySelectStyles}
                    menuPlacement="auto"
                    placeholder="Select Property"
                    components={{
                      DropdownIndicator: UserSelectDropDownIndicator,
                      IndicatorSeparator: () => null,
                      SingleValue: PropertySelectSingleValue,
                      Option: PropertySelectOption,
                    }}
                  />
                </div>
                <div className="w-full mr-16">
                  {isFirstFilter && (
                    <label className="mb-14 block text-gray-500 font-medium" htmlFor="assigner">
                      Behavior
                    </label>
                  )}
                  <Select
                    options={
                      filter.filter !== 'status'
                        ? [
                            { label: 'is', value: 'is' },
                            { label: 'is not', value: 'is_not' },
                            { label: 'is set', value: 'is_set' },
                            { label: 'is not set', value: 'is_not_set' },
                          ]
                        : [{ label: 'is', value: 'is' }]
                    }
                    value={
                      filter.behaviour
                        ? {
                            label: behaviourLabelIcon[filter.behaviour].label,
                            value: filter.behaviour,
                          }
                        : undefined
                    }
                    onChange={handleChangeBehaviour(index, isIncomplete)}
                    menuPortalTarget={document.body}
                    styles={PropertySelectStyles}
                    menuPlacement="auto"
                    placeholder="Select Property"
                    components={{
                      DropdownIndicator: UserSelectDropDownIndicator,
                      IndicatorSeparator: () => null,
                      SingleValue: PropertySelectSingleValue,
                      Option: PropertySelectOption,
                    }}
                  />
                </div>
                {['is', 'is_not'].includes(filter.behaviour) && (
                  <div className="w-full">
                    {isFirstFilter && (
                      <label className="mb-14 block text-gray-500 font-medium" htmlFor="assigner">
                        Value
                      </label>
                    )}
                    {filter.filter === 'priority' && (
                      <Select
                        options={[
                          { label: 'High', value: 'high' },
                          { label: 'Medium', value: 'medium' },
                          { label: 'Low', value: 'low' },
                        ]}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                        styles={PropertySelectStyles}
                        onChange={handleChangeProperty(index, isIncomplete)}
                        components={{
                          DropdownIndicator: UserSelectDropDownIndicator,
                          IndicatorSeparator: () => null,
                          SingleValue: PropertySelectSingleValue,
                          Option: PropertySelectOption,
                        }}
                      />
                    )}
                    {['assigner', 'assignee'].includes(filter.filter) && (
                      <Select
                        options={assigneeOptions}
                        value={filter.property && getUserValue(filter.property, assigneeOptions)}
                        menuPortalTarget={document.body}
                        placeholder={<UserSelectPlaceholder />}
                        menuPlacement="auto"
                        styles={PropertySelectStyles}
                        onChange={handleChangeProperty(index, isIncomplete)}
                        components={{
                          DropdownIndicator: UserSelectDropDownIndicator,
                          IndicatorSeparator: () => null,
                          SingleValue: UserSelectSingleValue,
                          Option: UserSelectOption,
                          MultiValueLabel: UserSelectMultiValueLabel,
                        }}
                      />
                    )}
                    {filter.filter === 'status' && (
                      <Select
                        options={[
                          { label: 'Complete', value: 'true' },
                          { label: 'Incomplete', value: 'false' },
                        ]}
                        menuPlacement="auto"
                        menuPortalTarget={document.body}
                        styles={PropertySelectStyles}
                        onChange={handleChangeProperty(index, isIncomplete)}
                        components={{
                          DropdownIndicator: UserSelectDropDownIndicator,
                          IndicatorSeparator: () => null,
                          SingleValue: PropertySelectSingleValue,
                          Option: PropertySelectOption,
                        }}
                      />
                    )}
                    {['label'].includes(filter.filter) && (
                      <Select
                        onChange={handleChangeProperty(index, isIncomplete)}
                        menuPortalTarget={document.body}
                        placeholder="Select label"
                        menuPlacement="auto"
                        value={filter.property && getUserValue(filter.property, assigneeOptions)}
                        options={labels}
                        components={{
                          DropdownIndicator: UserSelectDropDownIndicator,
                          IndicatorSeparator: () => null,
                          NoOptionsMessage: LabelSelectNoOptions,
                          SingleValue: LabelSelectSingleValue,
                          Option: LabelSelectOption,
                        }}
                        styles={labelSelectStyles}
                      />
                    )}
                  </div>
                )}
                <button
                  className={cn('ml-10', { 'mt-30': isFirstFilter })}
                  onClick={handleRemoveFilter(index, isIncomplete)}
                >
                  <CloseIcon className="text-gray-500 text-xs" />
                </button>
              </div>
            );
          });
        })}
      </div>
      <div className="py-14 text-gray-600">
        <MoreActionMenu
          placement="bottom"
          icon={
            <span className="text-primary-500 text-sm flex items-center hover:bg-primary-100">
              <AddIcon />
              <span className="ml-17">Add a filter</span>
            </span>
          }
          onClickMenuItem={handleOnAddFilter}
          menuItems={[
            { label: 'Assigner', value: 'assigner', icon: <PersonIcon size={24} /> },
            // { label: 'Assign date', value: 'assignDate', icon: <DateIcon size={24}  /> },
            { label: 'Assigned to', value: 'assignee', icon: <PersonIcon size={24} /> },
            { label: 'Priority', value: 'priority', icon: <SelectIcon size={24} /> },
            { label: 'Status', value: 'status', icon: <FilterTickMarkIcon size={24} /> },
            { label: 'Label', value: 'label', icon: <LabelIcon size={24} /> },
          ]}
        />
      </div>
    </div>
  );
};

const getUserValue = (memberId, options) => {
  const user = options.find((obj) => obj.memberId === memberId);
  return user;
};

const propertyLabelIcon = {
  assigner: {
    label: 'Assigner',
    icon: <PersonIcon className="text-xl" />,
  },
  assignee: {
    label: 'Assignee',
    icon: <PersonIcon className="text-xl" />,
  },
  priority: {
    label: 'Priority',
    icon: <SelectIcon className="text-xl" />,
  },
  label: {
    label: 'Label',
    icon: <LabelIcon className="text-xl" />,
  },
  status: {
    label: 'Status',
    icon: <FilterTickMarkIcon className="text-xl" />,
  },
};

const behaviourLabelIcon = {
  is: {
    label: 'is',
  },
  is_not: {
    label: 'is not',
  },
  is_set: {
    label: 'is set',
  },
  is_not_set: {
    label: 'is not set',
  },
};
